import { type ReactNode } from 'react';
import { Button, type ButtonProps } from '../components/Button/index.js';
import { useFormApi } from './FormContext.js';

export interface FormSubmitProps extends Omit<ButtonProps, 'type' | 'loading'> {
  children: ReactNode;
}

/** Submit button bound to the surrounding Form — reflects the form's
 *  `submitting` state via the Button's `loading` spinner. Spec 12 §14. */
export function FormSubmit(props: FormSubmitProps): ReactNode {
  const api = useFormApi();
  return <Button {...props} type="submit" loading={api.submitting} />;
}

export interface FormErrorProps {
  /** Optional title above the error list. */
  title?: ReactNode;
  className?: string;
}

/** Banner showing all field errors — render near the top of long forms so
 *  users don't have to scroll searching for the red field. */
export function FormError(props: FormErrorProps): ReactNode {
  const api = useFormApi();
  const entries = Object.entries(api.errors).filter(([, error]) => error !== undefined);
  if (entries.length === 0) return null;
  const classes = ['vq-form-error', props.className].filter(Boolean).join(' ');
  return (
    <div className={classes} role="alert" data-component="FormError">
      {props.title !== undefined ? <div className="vq-form-error__title">{props.title}</div> : null}
      <ul className="vq-form-error__list">
        {entries.map(([name, error]) => (
          <li key={name}>
            <strong>{name}: </strong>
            {Array.isArray(error) ? error.join(', ') : error}
          </li>
        ))}
      </ul>
    </div>
  );
}
